import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { SharedService } from 'src/app/shared.service';
import { B2BService } from 'src/app/b2-b.service';
import { TranslateService } from '@ngx-translate/core';
import { Router, ActivatedRoute } from "@angular/router";
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-partner',
  templateUrl: './partner.component.html',
  styleUrls: ['./partner.component.css']
})
export class PartnerComponent implements OnInit {

// Multi-step flow control
  currentStep: number = 1; // Steps: 1=Register/Login, 2=Billing, 3=Shipping, 4=Agents

  // Step A: Registration/Login
  User: any;
  RememberMeData = {};
  rememberMe: boolean = false;
  isRegistering: boolean = false;
  showExistingUserLogin: boolean = false;
  existingUserEmail: string = '';
  loginUserName: string = '';
  loginPassword: string = '';
  logoImage: string = '';
  LoginResponse: any = {};
  ShowMFA: boolean = false;
  MFAToken: string = '';
  hide: boolean = true;
  hideCPassword: boolean = true;
  loading: boolean = false;

  // Step B: Billing Information (Partner)
  Partner: any = {
    BillingAddress: {},
    Bank: {},
    ContactInfo: {}
  };
  Countries: any[] = [];
  Banks: any[] = [];
  showBillingCountryDropdown: boolean = false;
  showBankDropdown: boolean = false;

  // Add New Bank feature
  showAddBankForm: boolean = false;
  newBankName: string = '';
  newBankSwift: string = '';
  addingBank: boolean = false;

  // Step C: Shipping Information
  CurrentShippingAddress: any = {};
  showShippingCountryDropdown: boolean = false;

  // Shipping Address List & Popup
  selectedShippingAddressIndex: number = -1;  // Index of selected address in list
  showShippingAddressPopup: boolean = false;  // Controls popup visibility
  editingShippingAddress: any = {};           // Address being edited in popup
  isNewShippingAddress: boolean = false;      // True if adding new, false if editing
  savingShippingAddress: boolean = false;     // Loading state for save operation
  showPopupShippingCountryDropdown: boolean = false;  // Country dropdown in popup
  popupShippingCountrySearchTerm: string = '';        // Search term for popup country dropdown

  // Step D: Agents Information
  PartnerAgents: any[] = [];
  PartnerAgent: any = null;
  Departments: any[] = [];
  ShippingAddresses: any[] = [];
  showAgentForm: boolean = false;
  showAgentShippingDropdown: boolean = false;
  showDepartmentDropdown: boolean = false;
  isEditingAgent: boolean = false;
  editingAgentIndex: number = -1;

  // Agent Notification Phases
  showDefaultPhasesPopup: boolean = false;
  selectedDefaultPhaseAgent: any = null;
  defaultPhasesMasterData: any[] = [];
  selectedDefaultPhaseIDs: number[] = [];
  savingDefaultPhases: boolean = false;

  // Default Company (loaded from API)
  DefaultCompany: any = null;

  // Session Storage Key
  private readonly SESSION_KEY = 'b2b_register_session';

  // Restart confirmation
  showRestartConfirm: boolean = false;

  // Existing Partner Mode (loaded from API)
  isExistingPartner: boolean = false;

  // Public Partner Edit Mode (via URL parameter)
  partnerId: string = '';
  showErrorModal: boolean = false;
  errorMessage: string = '';
  isPublicMode: boolean = false;
  partnerLoaded: boolean = false;

  // Static System User for public API calls
  private readonly SYSTEM_USER = {
    Id: 'd0a6fe45467345caa8d046569f9a41687776e07d9d864d588912d32ebafd80e4',
    Email: 'office@foxline.ro'
  };

  // Registration Form
  signin: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.email, Validators.required]),
    password: new FormControl('', [Validators.required, Validators.minLength(3)]),
    confirmPassword: new FormControl('', [Validators.required, Validators.minLength(3)]),
    username: new FormControl('', [Validators.required, Validators.minLength(3)]),
    phone: new FormControl('', [Validators.required, Validators.minLength(3)]),
    name: new FormControl('', [Validators.required, Validators.minLength(3)]),
  });

  get emailInput() { return this.signin.get('email'); }
  get passwordInput() { return this.signin.get('password'); }
  get confirmPasswordInput() { return this.signin.get('confirmPassword'); }
  get usernameInput() { return this.signin.get('username'); }
  get nameInput() { return this.signin.get('name'); }
  get phoneInput() { return this.signin.get('phone'); }

  constructor(
    private service: SharedService,
    private b2bService: B2BService,
    private cookieService: CookieService,
    public translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private _snackBar: MatSnackBar
  ) {
    translate.addLangs(['en', 'ro', 'de', 'es', 'fr', 'it', 'pl', 'pt']);
    translate.setDefaultLang('en');
    const browserLang = this.getLanguage();
    translate.use(browserLang.match(/en|ro|de|es|fr|it|pl|pt/) ? browserLang : 'en');
  }

  getLanguage(): string {
    if (localStorage) {
      return localStorage['language'] || "";
    }
    return "";
  }

  setLanguage(language: string) {
    if (localStorage) {
      localStorage['language'] = language;
    }
  }

  ngOnInit(): void {
    this.rememberMe = false;
    this.isRegistering = false;
    this.currentStep = 1;

    this.logoImage = this.service.ImagesServiceUrl + "/B2B/foxline_automation_icon.png";
    this.SetDefaultLanguage();

    // Check for partner ID in URL query parameter (public mode)
    this.route.queryParams.subscribe(params => {
      if (params['id']) {
        // Public mode: Load partner by ID
        this.partnerId = params['id'];
        this.isPublicMode = true;
        this.User = this.SYSTEM_USER; // Use static system user for all API calls
        this.validateAndLoadPartner();
      } else {
        // No partner ID provided - show error
        this.isPublicMode = true;
        this.showErrorModal = true;
        this.errorMessage = 'PartnerIdRequired';
        this.partnerLoaded = true;
      }
    });
  }

  /**
   * Validates the partner ID from URL and loads partner data if found
   */
  validateAndLoadPartner() {
    this.loading = true;

    var request = {
      PartnerID: this.partnerId,
      User: this.User,
      Language: this.getLanguage(),
      WithAllData: true
    };

    this.service.GetPartner(request).subscribe(response => {
      if (response["Status"] == 1 && response["Partner"]) {
        // Partner found - load data for editing
        this.Partner = response["Partner"];
        this.isExistingPartner = true;
        this.partnerLoaded = true;
        this.showErrorModal = false;

        // Ensure nested objects exist
        if (!this.Partner.BillingAddress) {
          this.Partner.BillingAddress = {};
        }
        if (!this.Partner.Bank) {
          this.Partner.Bank = {};
        }
        if (!this.Partner.ContactInfo) {
          this.Partner.ContactInfo = {};
        }

        // Pre-populate shipping addresses if available
        if (this.Partner.ShippingAddresses && this.Partner.ShippingAddresses.length > 0) {
          this.ShippingAddresses = this.Partner.ShippingAddresses;
          this.CurrentShippingAddress = { ...this.Partner.ShippingAddresses[0] };
        }

        // Pre-populate agents if available
        if (this.Partner.PartnerAgents && this.Partner.PartnerAgents.length > 0) {
          this.PartnerAgents = this.Partner.PartnerAgents;
        }

        // Load master data for form dropdowns
        this.loadMasterData();

        // Load company using Partner's Company ID
        if (this.Partner.Company && this.Partner.Company.ID) {
          this.loadDefaultCompany(this.Partner.Company.ID);
        }

        // Fetch contractual conditions
        this.getPartnerContractualConditions();

        // Fetch partner agents separately
        this.getPartnerAgents();

        // Start at Billing tab (Step 2)
        this.currentStep = 2;
      } else {
        // Partner not found - show error modal
        this.showErrorModal = true;
        this.errorMessage = 'PartnerNotFound';
        this.partnerLoaded = true;
      }

      this.loading = false;
    }, () => {
      // API error - show error modal
      this.showErrorModal = true;
      this.errorMessage = 'PartnerLoadError';
      this.partnerLoaded = true;
      this.loading = false;
    });
  }

  loadDefaultCompany(companyId: number) {
    var request = {
      CompanyID: companyId,
      User: this.User,
      Language: this.getLanguage()
    };

    this.service.GetCompanyByID(request).subscribe(response => {
      if (response["Status"] == 1) {
        this.DefaultCompany = response["Company"];
      }
    });
  }

  SetDefaultLanguage() {
    if (this.getLanguage() == '') {
      this.setLanguage("en");
    }
  }

  DeleteRememberedCredentials() {
    if (!this.rememberMe == false) {
      this.cookieService.delete("RememberUser", "/");
    }
  }

  openSnackBar(message: string, action: string, designClass: string) {
    this._snackBar.open(message, action, {
      duration: 5000,
      panelClass: [designClass]
    });
  }

  // ============= STEP A: REGISTRATION / LOGIN =============

  RegisterNewUser() {
    var request = {
      Email: this.signin.controls['email'].value,
      Password: this.signin.controls['password'].value,
      ConfirmPassword: this.signin.controls['confirmPassword'].value,
      username: this.signin.controls['username'].value,
      phone: this.signin.controls['phone'].value,
      UIName: this.signin.controls['name'].value,
      Language: this.getLanguage()
    };

    this.loading = true;

    this.service.RegisterNewUser(request).subscribe(response => {
      if (response["Status"] != 1) {
        // Check if user already exists (Status code for existing user)
        if (response["Status"] == -2 || (response["Message"] && response["Message"].toLowerCase().includes('exist'))) {
          this.showExistingUserLogin = true;
          this.existingUserEmail = this.signin.controls['email'].value;
          this.loginUserName = this.signin.controls['email'].value;
          this.loginPassword = '';
          this.isRegistering = false;
        } else {
          this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
        }
      } else {
        // Registration successful - auto login
        this.loginUserName = this.signin.controls['email'].value;
        this.loginPassword = this.signin.controls['password'].value;
        this.rememberMe = true;
        this.LoginAfterRegistration();
      }
      this.loading = false;
    });
  }

  LoginAfterRegistration() {
    this.LoginResponse = {};

    var request = {
      username: this.loginUserName,
      email: this.loginUserName,
      password: this.loginPassword,
      Language: this.getLanguage()
    };

    this.loading = true;

    this.service.Login(request).subscribe(response => {
      if (this.rememberMe) {
        this.cookieService.set('RememberUser', JSON.stringify(request), 5, "/");
      }

      if (response["Status"] != 1) {
        this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
        this.loading = false;
      } else {
        this.User = response["User"];
        this.LoginResponse["Status"] = response["Status"];
        this.LoginResponse["Message"] = response["Message"];
        this.setLanguage(this.User.Settings.Language.Name.toLowerCase());
        this.translate.use(this.User.Settings.Language.Name.toLowerCase());
        localStorage.setItem('User', JSON.stringify(this.User));

        // Check for existing partner before proceeding
        this.checkExistingPartnerAndProceed();
      }
    });
  }

  Login() {
    this.LoginResponse = {};

    var request = {
      username: this.loginUserName,
      email: this.loginUserName,
      password: this.loginPassword,
      Language: this.getLanguage()
    };

    this.loading = true;

    this.service.Login(request).subscribe(response => {
      if (this.rememberMe) {
        this.cookieService.set('RememberUser', JSON.stringify(request), 5, "/");
      }

      if (response["Status"] != 1) {
        this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
        this.loading = false;
      } else {
        this.User = response["User"];
        this.LoginResponse["Status"] = response["Status"];
        this.LoginResponse["Message"] = response["Message"];
        this.setLanguage(this.User.Settings.Language.Name.toLowerCase());
        this.translate.use(this.User.Settings.Language.Name.toLowerCase());
        localStorage.setItem('User', JSON.stringify(this.User));

        // Check for existing partner before proceeding
        this.checkExistingPartnerAndProceed();
      }
    });
  }

  VerifyMFAToken() {
    var request = {
      User: this.User,
      Language: this.getLanguage(),
      Token: this.MFAToken
    };

    this.loading = true;

    this.service.VerifyMFAToken(request).subscribe(response => {
      if (response["Status"] != 1) {
        this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
        this.loading = false;
      } else {
        this.cookieService.set('MFAuthToken', JSON.stringify(true), 5, "/");
        localStorage.setItem('User', JSON.stringify(this.User));
        // Move to Step B after MFA (no redirect)
        this.proceedToStepB();
      }
    });
  }

  SendRecoverPasswordEmail() {
    var request = {
      UserName: this.loginUserName,
      Language: this.getLanguage()
    };

    this.loading = true;

    this.service.SendRecoverPasswordEmail(request).subscribe(response => {
      if (response["Status"] != 1) {
        this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      } else {
        this.openSnackBar(response["Message"], "OK", "light-info-snackbar");
      }
      this.loading = false;
    });
  }

  // ============= CHECK EXISTING PARTNER =============

  checkExistingPartnerAndProceed() {
    var request = {
      User: this.User,
      Language: this.getLanguage() || 'en'
    };

    this.b2bService.GetB2BUserConnectedPartner(request).subscribe(response => {
      if (response["Status"] == 1 && response["Partner"]) {
        // Existing partner found - pre-populate form
        this.Partner = response["Partner"];
        this.isExistingPartner = true;

        // Ensure nested objects exist
        if (!this.Partner.BillingAddress) {
          this.Partner.BillingAddress = {};
        }
        if (!this.Partner.Bank) {
          this.Partner.Bank = {};
        }
        if (!this.Partner.ContactInfo) {
          this.Partner.ContactInfo = {};
        }

        // Pre-populate shipping addresses if available
        if (this.Partner.ShippingAddresses && this.Partner.ShippingAddresses.length > 0) {
          this.ShippingAddresses = this.Partner.ShippingAddresses;
          this.CurrentShippingAddress = { ...this.Partner.ShippingAddresses[0] };
        }

        // Pre-populate agents if available from Partner object
        if (this.Partner.PartnerAgents && this.Partner.PartnerAgents.length > 0) {
          this.PartnerAgents = this.Partner.PartnerAgents;
        }

        // Fetch contractual conditions for this partner
        this.getPartnerContractualConditions();

        // Load master data (Countries, Banks) for the form dropdowns
        this.loadMasterData();

        // Fetch partner agents separately (in case they're not included in Partner object)
        this.getPartnerAgents();

        // Determine which step to go to based on existing data
        this.determineStepBasedOnPartnerData();
      } else {
        // No existing partner - proceed to Step B (new registration)
        this.isExistingPartner = false;
        this.proceedToStepB();
      }
    });
  }

  getPartnerContractualConditions() {
    if (!this.Partner?.ID) return;

    var request = {
      User: this.User,
      Language: this.getLanguage() || 'en',
      PartnerID: this.Partner.ID
    };

    this.service.GetPartnerContractualConditions(request).subscribe(response => {
      if (response["Status"] == 1) {
        this.Partner.ContractualConditions = response["ContractualConditions"];
      }
    });
  }

  getPartnerAgents() {
    if (!this.Partner?.ID) return;

    var request = {
      User: this.User,
      Language: this.getLanguage() || 'en',
      PartnerID: this.Partner.ID
    };

    this.service.GetPartnerAgents(request).subscribe(response => {
      if (response["Status"] == 1) {
        this.PartnerAgents = response["PartnerAgents"] || [];
        // Also store in Partner object for consistency
        this.Partner.PartnerAgents = this.PartnerAgents;
        this.loadAllAgentsDefaultPhases();
      }
    });
  }

  determineStepBasedOnPartnerData() {
    // For existing partners: ALWAYS start at Billing tab (Step 2)
    // They can freely navigate to other tabs from there

    // Pre-load shipping addresses and agents if available
    if (this.Partner.ShippingAddresses && this.Partner.ShippingAddresses.length > 0) {
      this.ShippingAddresses = this.Partner.ShippingAddresses;
    }
    if (this.Partner.PartnerAgents && this.Partner.PartnerAgents.length > 0) {
      this.PartnerAgents = this.Partner.PartnerAgents;
    }

    // Always go to Step 2 (Billing) for existing partners
    this.currentStep = 2;
    this.saveSession();
    this.InitializeNewPartnerWithExistingData();
  }

  InitializeNewPartnerWithExistingData() {
    // If master data (Countries, Banks) is already loaded, skip the API call
    if (this.Countries?.length > 0 && this.Banks?.length > 0) {
      this.loading = false;
      return;
    }

    // Load master data using the correct endpoint that returns Countries and Banks
    var request = {
      User: this.User,
      Language: this.getLanguage()
    };

    this.service.GetMasterData(request).subscribe(response => {
      if (response["Status"] == 1) {
        this.Countries = response["Countries"] || [];
        this.Banks = response["Banks"] || [];
      }
      this.loading = false;
    }, () => {
      this.loading = false;
    });
  }

  // ============= STEP NAVIGATION =============

  canNavigateToStep(step: number): boolean {
    // Step 1 is always locked (login/register step) - cannot go back to it
    if (step === 1) {
      return false;
    }

    // For existing partners: can navigate to any step (2, 3, 4)
    if (this.isExistingPartner && this.Partner?.ID) {
      return step >= 2 && step <= 4;
    }

    // For new users: can only go back to previously completed steps
    // or stay on current step
    if (step < this.currentStep && step >= 2) {
      return true;
    }

    return false;
  }

  isStepCompleted(step: number): boolean {
    return this.currentStep > step;
  }

  isStepEnabled(step: number): boolean {
    // Step 1 is special - only enabled when on step 1
    if (step === 1) {
      return this.currentStep === 1;
    }

    // For existing partners: all steps 2-4 are enabled
    if (this.isExistingPartner && this.Partner?.ID) {
      return step >= 2 && step <= 4;
    }

    // For new users: current step and previous completed steps are enabled
    return step <= this.currentStep && step >= 2;
  }

  closeAllDropdowns() {
    this.showBillingCountryDropdown = false;
    this.showBankDropdown = false;
    this.showShippingCountryDropdown = false;
    this.showPopupShippingCountryDropdown = false;
    this.showAgentShippingDropdown = false;
    this.showDepartmentDropdown = false;
  }

  navigateToStep(step: number) {
    if (!this.canNavigateToStep(step)) {
      return;
    }
    // Don't navigate if already on this step
    if (this.currentStep === step) {
      return;
    }

    this.loading = true;
    this.currentStep = step;
    this.saveSession();

    // Reset dropdown visibility flags when switching tabs
    this.closeAllDropdowns();

    // Initialize the appropriate step
    switch (step) {
      case 2:
        // Step B - Billing: load master data if not already present
        this.InitializeNewPartnerWithExistingData();
        break;
      case 3:
        // Step C - Shipping: initialize shipping if needed
        if (!this.CurrentShippingAddress?.ID && this.ShippingAddresses?.length > 0) {
          this.CurrentShippingAddress = { ...this.ShippingAddresses[0] };
        }
        this.loading = false;
        break;
      case 4:
        // Step D - Agents: ensure agents are loaded
        if (this.Partner.ShippingAddresses && this.Partner.ShippingAddresses.length > 0) {
          this.ShippingAddresses = this.Partner.ShippingAddresses;
        }
        // Fetch partner agents if not already loaded
        if (!this.PartnerAgents || this.PartnerAgents.length === 0) {
          this.fetchPartnerAgentsForStep4();
        } else {
          this.loading = false;
        }
        break;
      default:
        this.loading = false;
    }
  }

  // ============= STEP B: BILLING INFORMATION =============

  proceedToStepB() {
    this.loading = true;
    this.currentStep = 2;
    this.saveSession();
    this.InitializeNewPartner();
  }

  InitializeNewPartner() {
    var request = {
      User: this.User,
      Language: this.getLanguage(),
    };

    this.service.InitializeNewPartner(request).subscribe(response => {
      if (response["Status"] != 1) {
        this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      } else {
        this.Partner = response["Partner"];
        this.Countries = response["Countries"] || [];
        this.Banks = response["Banks"] || [];

        // Initialize billing address if not present
        if (!this.Partner.BillingAddress) {
          this.Partner.BillingAddress = {};
        }
        if (!this.Partner.Bank) {
          this.Partner.Bank = {};
        }
        if (!this.Partner.ContactInfo) {
          this.Partner.ContactInfo = {};
        }
      }
      this.loading = false;
    });
  }

  GetPartnersMasterData() {
    var request = {
      User: this.User,
      Language: this.getLanguage(),
    };

    this.service.GetPartnersMasterData(request).subscribe(response => {
      if (response["Status"] == 1) {
        this.Countries = response["Countries"] || [];
        this.Banks = response["Banks"] || [];
      }
    });
  }

  validateBillingInfo(): boolean {
    // Validate mandatory billing fields
    if (!this.Partner.Name || this.Partner.Name.trim() === '') return false;
    if (!this.Partner.VAT || this.Partner.VAT.trim() === '') return false;
    if (!this.Partner.BillingAddress?.Country) return false;
    if (!this.Partner.BillingAddress?.StateProvince || this.Partner.BillingAddress.StateProvince.trim() === '') return false;
    if (!this.Partner.BillingAddress?.City || this.Partner.BillingAddress.City.trim() === '') return false;
    if (!this.Partner.BillingAddress?.AddressLine || this.Partner.BillingAddress.AddressLine.trim() === '') return false;
    if (!this.Partner.BillingAddress?.ZipCode || this.Partner.BillingAddress.ZipCode.trim() === '') return false;
    return true;
  }

  proceedToStepC() {
    if (!this.validateBillingInfo()) {
      this.openSnackBar(this.translate.instant('AllFieldsRequired'), "OK", "alert-info-snackbar");
      return;
    }

    this.loading = true;

    // Set required Company for the API (use DefaultCompany loaded from API, fallback to hardcoded)
    this.Partner.Company = this.DefaultCompany || { ID: 1, Name: "Foxline SRL" };

    // Sanitize Bank: if no bank was selected (empty object), send { ID: 0 } instead of null
    // Backend accesses request.Partner.Bank.ID unconditionally — null would cause NullReferenceException
    if (this.Partner.Bank && !this.Partner.Bank.ID && !this.Partner.Bank.Name) {
      this.Partner.Bank = { ID: 0 };
    }

    // Ensure User has Rights.AssignedCompanies with IsSelected = true
    // so backend ExtractCompany() can resolve the active company for VAT validation
    var user = { ...this.User };
    if (!user.Rights) {
      user.Rights = { AssignedCompanies: [] };
    }
    if (!user.Rights.AssignedCompanies || user.Rights.AssignedCompanies.length === 0) {
      var company = this.DefaultCompany || { ID: 1, Name: 'Foxline SRL' };
      user.Rights.AssignedCompanies = [{ ...company, IsSelected: true }];
    } else {
      // Ensure at least one company has IsSelected = true
      var hasSelected = user.Rights.AssignedCompanies.some(c => c.IsSelected === true);
      if (!hasSelected) {
        user.Rights.AssignedCompanies[0].IsSelected = true;
      }
    }

    var request = {
      User: user,
      Language: this.getLanguage(),
      Partner: this.Partner,
    };

    if (this.isExistingPartner && this.Partner.ID) {
      // Existing partner: update billing info, then navigate to Step 3
      this.service.UpdatePartner(request).subscribe(response => {
        if (response["Status"] != 1) {
          this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
          this.loading = false;
        } else {
          this.Partner = response["Partner"];
          this.currentStep = 3;
          this.saveSession();
          this.initializeShippingAddress();
        }
      }, () => {
        this.openSnackBar("An error occurred. Please try again.", "OK", "alert-info-snackbar");
        this.loading = false;
      });
    } else {
      // New partner: create first, then navigate to Step 3
      this.service.AddNewPartner(request).subscribe(response => {
        if (response["Status"] != 1) {
          this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
          this.loading = false;
        } else {
          this.Partner.ID = response["ID"];
          this.isExistingPartner = true;
          if(response["DuplicateDetected"]){
            this.openSnackBar(this.translate.instant("Management.DuplicatePartnerDetected"), "OK", "alert-danger-snackbar");
          }
          this.currentStep = 3;
          this.saveSession();
          this.initializeShippingAddress();
        }
      }, () => {
        this.openSnackBar("An error occurred. Please try again.", "OK", "alert-info-snackbar");
        this.loading = false;
      });
    }
  }

  // ============= STEP C: SHIPPING INFORMATION =============

  initializeShippingAddress() {
    // Generate initial shipping address from billing
    var request = {
      User: this.User,
      Language: this.getLanguage(),
      Partner: this.Partner
    };

    this.service.GenerateInitialShippingAddress(request).subscribe(response => {
      if (response["Status"] != 1) {
        this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
        // Initialize empty shipping address
        this.CurrentShippingAddress = {
          Country: this.Partner.BillingAddress?.Country || {},
          State: this.Partner.BillingAddress?.StateProvince || '',
          City: this.Partner.BillingAddress?.City || '',
          AddressLine: this.Partner.BillingAddress?.AddressLine || '',
          ZipCode: this.Partner.BillingAddress?.ZipCode || '',
          ContactPerson: this.Partner.ContactInfo?.Name || '',
          ContactCompany: this.Partner.Name || '',
          Email: this.Partner.ContactInfo?.Email || '',
          Phone: this.Partner.ContactInfo?.Phone || '',
          ActiveAddress: true,
          IsSelected: true
        };
      } else {
        this.Partner = response["Partner"];
        var addressesLength = this.Partner.ShippingAddresses?.length || 0;
        if (addressesLength > 0) {
          this.CurrentShippingAddress = this.Partner.ShippingAddresses[addressesLength - 1];
        } else {
          this.CurrentShippingAddress = {
            Country: {},
            State: '',
            City: '',
            AddressLine: '',
            ZipCode: '',
            ContactPerson: '',
            ContactCompany: '',
            Email: '',
            Phone: '',
            ActiveAddress: true,
            IsSelected: true
          };
        }
      }
      this.loading = false;
    });
  }

  validateShippingInfo(): boolean {
    if (!this.CurrentShippingAddress.Country?.ID) return false;
    if (!this.CurrentShippingAddress.State || this.CurrentShippingAddress.State.trim() === '') return false;
    if (!this.CurrentShippingAddress.City || this.CurrentShippingAddress.City.trim() === '') return false;
    if (!this.CurrentShippingAddress.AddressLine || this.CurrentShippingAddress.AddressLine.trim() === '') return false;
    if (!this.CurrentShippingAddress.ZipCode || this.CurrentShippingAddress.ZipCode.trim() === '') return false;
    if (!this.CurrentShippingAddress.ContactPerson || this.CurrentShippingAddress.ContactPerson.trim() === '') return false;
    if (!this.CurrentShippingAddress.ContactCompany || this.CurrentShippingAddress.ContactCompany.trim() === '') return false;
    if (!this.CurrentShippingAddress.Email || this.CurrentShippingAddress.Email.trim() === '') return false;
    if (!this.CurrentShippingAddress.Phone || this.CurrentShippingAddress.Phone.trim() === '') return false;
    return true;
  }

  // Validate shipping address in popup
  validatePopupShippingInfo(): boolean {
    if (!this.editingShippingAddress.Country?.ID) return false;
    if (!this.editingShippingAddress.State || this.editingShippingAddress.State.trim() === '') return false;
    if (!this.editingShippingAddress.City || this.editingShippingAddress.City.trim() === '') return false;
    if (!this.editingShippingAddress.AddressLine || this.editingShippingAddress.AddressLine.trim() === '') return false;
    if (!this.editingShippingAddress.ZipCode || this.editingShippingAddress.ZipCode.trim() === '') return false;
    if (!this.editingShippingAddress.ContactPerson || this.editingShippingAddress.ContactPerson.trim() === '') return false;
    if (!this.editingShippingAddress.ContactCompany || this.editingShippingAddress.ContactCompany.trim() === '') return false;
    if (!this.editingShippingAddress.Email || this.editingShippingAddress.Email.trim() === '') return false;
    if (!this.editingShippingAddress.Phone || this.editingShippingAddress.Phone.trim() === '') return false;
    return true;
  }

  // Select a shipping address from the list
  selectShippingAddress(address: any, index: number) {
    this.selectedShippingAddressIndex = index;
    this.CurrentShippingAddress = JSON.parse(JSON.stringify(address)); // Deep copy
  }

  // Open popup for adding new shipping address
  openNewShippingAddressPopup() {
    this.isNewShippingAddress = true;
    this.editingShippingAddress = {
      ID: 0,
      Country: this.Partner.BillingAddress?.Country || {},
      State: '',
      City: '',
      AddressLine: '',
      ZipCode: '',
      ContactPerson: '',
      ContactCompany: this.Partner.Name || '',
      Email: this.Partner.ContactInfo?.Email || '',
      Phone: this.Partner.ContactInfo?.Phone || '',
      ActiveAddress: true,
      IsSelected: false
    };
    this.showShippingAddressPopup = true;
  }

  // Open popup for editing existing shipping address
  openEditShippingAddressPopup(address: any, index: number) {
    this.isNewShippingAddress = false;
    this.selectedShippingAddressIndex = index;
    this.editingShippingAddress = JSON.parse(JSON.stringify(address)); // Deep copy
    this.showShippingAddressPopup = true;
  }

  // Close the shipping address popup
  closeShippingAddressPopup() {
    this.showShippingAddressPopup = false;
    this.editingShippingAddress = {};
    this.showPopupShippingCountryDropdown = false;
    this.popupShippingCountrySearchTerm = '';
  }

  // Get filtered countries for popup dropdown
  getFilteredPopupShippingCountries(): any[] {
    if (!this.popupShippingCountrySearchTerm) return this.Countries;
    const term = this.popupShippingCountrySearchTerm.toLowerCase();
    return this.Countries.filter(c => c.Name?.toLowerCase().includes(term));
  }

  // Select country in popup dropdown
  selectPopupShippingCountry(country: any) {
    this.editingShippingAddress.Country = country;
    this.showPopupShippingCountryDropdown = false;
    this.popupShippingCountrySearchTerm = '';
  }

  // Close popup country dropdown
  closePopupShippingCountryDropdown() {
    this.showPopupShippingCountryDropdown = false;
    this.popupShippingCountrySearchTerm = '';
  }

  // Save shipping address from popup (Add or Update)
  saveShippingAddressFromPopup() {
    if (!this.validatePopupShippingInfo()) {
      this.openSnackBar(this.translate.instant('AllFieldsRequired'), "OK", "alert-info-snackbar");
      return;
    }

    this.savingShippingAddress = true;

    var request = {
      User: this.User,
      Language: this.getLanguage(),
      ShippingAddress: this.editingShippingAddress,
      PartnerID: this.Partner.ID
    };

    this.service.AddUpdateShippingAddress(request).subscribe(response => {
      if (response["Status"] != 1) {
        this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      } else {
        const savedAddress = response["ShippingAddress"];

        if (this.isNewShippingAddress) {
          // Add new address to the Partner's ShippingAddresses array
          if (!this.Partner.ShippingAddresses) {
            this.Partner.ShippingAddresses = [];
          }
          this.Partner.ShippingAddresses.push(savedAddress);
          // Select the newly added address
          this.selectedShippingAddressIndex = this.Partner.ShippingAddresses.length - 1;
          this.CurrentShippingAddress = JSON.parse(JSON.stringify(savedAddress));
          this.openSnackBar(this.translate.instant('AddressAddedSuccessfully') || 'Address added successfully', "OK", "light-info-snackbar");
        } else {
          // Update existing address in the array
          if (this.Partner.ShippingAddresses && this.selectedShippingAddressIndex >= 0) {
            this.Partner.ShippingAddresses[this.selectedShippingAddressIndex] = savedAddress;
            this.CurrentShippingAddress = JSON.parse(JSON.stringify(savedAddress));
          }
          this.openSnackBar(this.translate.instant('AddressUpdatedSuccessfully') || 'Address updated successfully', "OK", "light-info-snackbar");
        }

        // Also update ShippingAddresses for Step 4
        this.ShippingAddresses = this.Partner.ShippingAddresses || [];
        this.saveSession();
        this.closeShippingAddressPopup();
      }
      this.savingShippingAddress = false;
    });
  }

  proceedToStepD() {
    // If no shipping addresses are persisted yet but we have a valid CurrentShippingAddress (e.g. auto-generated from billing),
    // auto-save it before proceeding instead of blocking the user.
    // Also handles case where GenerateInitialShippingAddress returned addresses without persisting them (no ID).
    var hasPersistedAddress = this.Partner.ShippingAddresses?.some(a => a.ID) || false;
    var addressToSave = this.CurrentShippingAddress || (this.Partner.ShippingAddresses?.length > 0 ? this.Partner.ShippingAddresses[0] : null);

    if (!hasPersistedAddress && addressToSave?.Country?.ID && addressToSave?.City) {
      this.loading = true;
      var request = {
        User: this.User,
        Language: this.getLanguage(),
        ShippingAddress: addressToSave,
        PartnerID: this.Partner.ID
      };

      this.service.AddUpdateShippingAddress(request).subscribe(response => {
        if (response["Status"] != 1) {
          this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
          this.loading = false;
        } else {
          const savedAddress = response["ShippingAddress"];
          // Replace unpersisted addresses with the saved one
          this.Partner.ShippingAddresses = [savedAddress];
          this.selectedShippingAddressIndex = 0;
          this.CurrentShippingAddress = JSON.parse(JSON.stringify(savedAddress));
          this.ShippingAddresses = this.Partner.ShippingAddresses;
          this.currentStep = 4;
          this.saveSession();
          this.loading = false;
        }
      }, () => {
        this.openSnackBar("An error occurred. Please try again.", "OK", "alert-info-snackbar");
        this.loading = false;
      });
      return;
    }

    // Check if user has at least one shipping address - that's all we need
    if (!this.Partner.ShippingAddresses || this.Partner.ShippingAddresses.length === 0) {
      this.openSnackBar(this.translate.instant('PleaseAddAtLeastOneShippingAddress') || 'Please add at least one shipping address', "OK", "alert-info-snackbar");
      return;
    }

    // Update ShippingAddresses for Step 4 and proceed
    this.ShippingAddresses = this.Partner.ShippingAddresses || [];
    this.currentStep = 4;
    this.saveSession();
  }

  // ============= STEP D: AGENTS INFORMATION =============

  fetchPartnerAgentsForStep4() {
    if (!this.Partner?.ID) {
      this.loading = false;
      return;
    }

    var request = {
      User: this.User,
      Language: this.getLanguage() || 'en',
      PartnerID: this.Partner.ID
    };

    this.service.GetPartnerAgents(request).subscribe(response => {
      if (response["Status"] == 1) {
        this.PartnerAgents = response["PartnerAgents"] || [];
        this.Partner.PartnerAgents = this.PartnerAgents;
        this.loadAllAgentsDefaultPhases();
      }
      this.loading = false;
    });
  }

  showAddAgentForm() {
    this.isEditingAgent = false;
    this.editingAgentIndex = -1;
    this.showAgentForm = true;
    this.InitializePartnerAgent();
  }

  editAgent(agent: any, index: number) {
    this.isEditingAgent = true;
    this.editingAgentIndex = index;
    this.showAgentForm = true;

    // Load master data (Departments, ShippingAddresses) if not already loaded
    this.loadAgentMasterData(() => {
      // Create a copy of the agent to edit
      this.PartnerAgent = { ...agent };
    });
  }

  loadAgentMasterData(callback?: () => void) {
    var request = {
      User: this.User,
      Language: this.getLanguage(),
      PartnerID: this.Partner.ID
    };

    this.loading = true;

    this.service.InitializePartnerAgent(request).subscribe(response => {
      if (response["Status"] == 1) {
        this.ShippingAddresses = response["ShippingAddresses"] || [this.CurrentShippingAddress];
        this.Departments = response["Departments"] || [];
      }
      this.loading = false;
      if (callback) {
        callback();
      }
    });
  }

  InitializePartnerAgent() {
    var request = {
      User: this.User,
      Language: this.getLanguage(),
      PartnerID: this.Partner.ID
    };

    this.loading = true;

    this.service.InitializePartnerAgent(request).subscribe(response => {
      if (response["Status"] != 1) {
        this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
        // Initialize empty agent
        this.PartnerAgent = {
          Name: '',
          Phone: '',
          Email: '',
          Department: null,
          AllocatedShippingAddress: this.CurrentShippingAddress || null
        };
      } else {
        this.PartnerAgent = response["PartnerAgent"];
        this.ShippingAddresses = response["ShippingAddresses"] || [this.CurrentShippingAddress];
        this.Departments = response["Departments"] || [];
      }
      this.loading = false;
    });
  }

  AddUpdatePartnerAgent() {
    if (!this.PartnerAgent.Name || this.PartnerAgent.Name.trim() === '' ||
        !this.PartnerAgent.Email || this.PartnerAgent.Email.trim() === '' ||
        !this.PartnerAgent.Phone || this.PartnerAgent.Phone.trim() === '') {
      this.openSnackBar(this.translate.instant('AllFieldsRequired'), "OK", "alert-info-snackbar");
      return;
    }

    var request = {
      User: this.User,
      Language: this.getLanguage(),
      PartnerAgent: this.PartnerAgent
    };

    this.loading = true;

    this.service.AddUpdatePartnerAgent(request).subscribe(response => {
      if (response["Status"] != 1) {
        this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      } else {
        const updatedAgent = response["PartnerAgent"];

        if (this.isEditingAgent && this.editingAgentIndex >= 0) {
          // Update existing agent in the list
          this.PartnerAgents[this.editingAgentIndex] = updatedAgent;
        } else {
          // Add new agent to the list
          this.PartnerAgents.push(updatedAgent);
        }

        this.openSnackBar(response["Message"], "OK", "light-info-snackbar");
        this.showAgentForm = false;
        this.PartnerAgent = null;
        this.isEditingAgent = false;
        this.editingAgentIndex = -1;
      }
      this.loading = false;
    });
  }

  cancelAgentForm() {
    this.showAgentForm = false;
    this.PartnerAgent = null;
    this.isEditingAgent = false;
    this.editingAgentIndex = -1;
  }

  // ============= COMPLETE REGISTRATION =============

  showCompletionModal: boolean = false;

  completeRegistration() {
    // Clear session storage on completion
    this.clearSession();
    // Show the Apple-style confirmation modal
    this.showCompletionModal = true;
  }

  closeCompletionAndWindow() {
    // Attempt to close the window/tab
    window.close();
    // Fallback: if window.close() is blocked by the browser, show a message
    setTimeout(() => {
      // If the window is still open after 500ms, it means the browser blocked window.close()
      this.openSnackBar(this.translate.instant('RegistrationComplete'), "OK", "light-info-snackbar");
    }, 500);
  }

  // ============= STEP NAVIGATION =============

  goBackToStep(step: number) {
    if (step < this.currentStep) {
      this.currentStep = step;
      this.saveSession();
    }
  }

  getStepTitle(): string {
    switch(this.currentStep) {
      case 1: return this.translate.instant('B2BRegister');
      case 2: return this.translate.instant('ViewBillingInformation');
      case 3: return this.translate.instant('ViewShippingInformation');
      case 4: return this.translate.instant('ViewAgentsInformation');
      default: return '';
    }
  }

  // ============= MASTER DATA =============

  loadMasterData() {
    var request = {
      User: this.User,
      Language: this.getLanguage()
    };

    this.service.GetMasterData(request).subscribe(response => {
      if (response["Status"] == 1) {
        this.Countries = response["Countries"] || [];
        this.Banks = response["Banks"] || [];
      }
    });
  }

  // ============= ADD NEW BANK =============

  toggleAddBankForm() {
    this.showAddBankForm = !this.showAddBankForm;
    this.newBankName = '';
    this.newBankSwift = '';
  }

  addNewBank() {
    if (!this.newBankName || this.newBankName.trim() === '') {
      this.openSnackBar(this.translate.instant('BankNameRequired'), "OK", "alert-info-snackbar");
      return;
    }

    if (!this.newBankSwift || this.newBankSwift.trim() === '') {
      this.openSnackBar(this.translate.instant('BankSwiftRequired'), "OK", "alert-info-snackbar");
      return;
    }

    var request = {
      User: this.User,
      Language: this.getLanguage(),
      Bank: {
        Name: this.newBankName.trim(),
        Swift: this.newBankSwift.trim()
      }
    };

    this.addingBank = true;

    this.service.AddBank(request).subscribe(response => {
      if (response["Status"] != 1) {
        this.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      } else {
        const newBank = response["Bank"];
        this.Banks.push(newBank);
        this.Partner.Bank = newBank;
        this.showAddBankForm = false;
        this.newBankName = '';
        this.newBankSwift = '';
        this.openSnackBar(this.translate.instant('BankAddedSuccessfully'), "OK", "light-info-snackbar");
        this.saveSession();
      }
      this.addingBank = false;
    });
  }

  cancelAddBank() {
    this.showAddBankForm = false;
    this.newBankName = '';
  }

  // ============= SESSION PERSISTENCE =============

  saveSession() {
    const sessionData = {
      currentStep: this.currentStep,
      User: this.User,
      Partner: this.Partner,
      CurrentShippingAddress: this.CurrentShippingAddress,
      PartnerAgents: this.PartnerAgents
    };
    sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(sessionData));
  }

  restoreSession() {
    const savedSession = sessionStorage.getItem(this.SESSION_KEY);
    if (savedSession) {
      try {
        const sessionData = JSON.parse(savedSession);

        // Restore User first
        if (sessionData.User) {
          this.User = sessionData.User;
          localStorage.setItem('User', JSON.stringify(this.User));
        }

        // Restore Partner with nested object initialization
        if (sessionData.Partner) {
          this.Partner = sessionData.Partner;
          // Ensure nested objects exist
          if (!this.Partner.BillingAddress) {
            this.Partner.BillingAddress = {};
          }
          if (!this.Partner.Bank) {
            this.Partner.Bank = {};
          }
          if (!this.Partner.ContactInfo) {
            this.Partner.ContactInfo = {};
          }
        }

        // Restore Shipping Address
        if (sessionData.CurrentShippingAddress) {
          this.CurrentShippingAddress = sessionData.CurrentShippingAddress;
        }

        // Restore Agents
        if (sessionData.PartnerAgents) {
          this.PartnerAgents = sessionData.PartnerAgents;
        }

        // Restore step ONLY if we have valid User (required for Step 2+)
        // Also require Partner with BillingAddress for Step 2+
        if (sessionData.currentStep && sessionData.currentStep > 1 && this.User && this.Partner) {
          this.currentStep = sessionData.currentStep;
        } else if (sessionData.currentStep && sessionData.currentStep > 1) {
          // Invalid session state - clear and start fresh
          this.clearSession();
          this.currentStep = 1;
        }
      } catch (e) {
        // If parse fails, clear corrupted session
        this.clearSession();
      }
    }
  }

  clearSession() {
    sessionStorage.removeItem(this.SESSION_KEY);
  }

  // ============= RESTART PROCESS =============

  showRestartConfirmation() {
    this.showRestartConfirm = true;
  }

  cancelRestart() {
    this.showRestartConfirm = false;
  }

  confirmRestart() {
    // Clear all session data
    this.clearSession();

    // Reset all form data
    this.currentStep = 1;
    this.User = null;
    this.Partner = {
      BillingAddress: {},
      Bank: {},
      ContactInfo: {}
    };
    this.CurrentShippingAddress = {};
    this.PartnerAgents = [];
    this.PartnerAgent = null;
    this.showAgentForm = false;
    this.showExistingUserLogin = false;
    this.isRegistering = false;
    this.ShowMFA = false;
    this.showRestartConfirm = false;

    // Reset registration form
    this.signin.reset();

    this.openSnackBar(this.translate.instant('ProcessRestarted'), "OK", "light-info-snackbar");
  }

  // ========== AGENT NOTIFICATION PHASES ==========

  loadAllAgentsDefaultPhases(): void {
    if (!this.PartnerAgents?.length) return;
    this.PartnerAgents.forEach((agent: any) => {
      if (agent.ID > 0) {
        const req = { User: this.User, Language: this.getLanguage() || 'en', AgentID: agent.ID };
        this.service.GetPartnerAgentDefaultPhases(req).subscribe((res: any) => {
          if (res.Status === 1) {
            agent.OrderPhases = res.Phases || [];
          }
        });
      }
    });
  }

  openDefaultPhasesPopup(agent: any): void {
    if (!agent?.ID || agent.ID <= 0) return;
    this.selectedDefaultPhaseAgent = agent;

    if (this.defaultPhasesMasterData.length === 0) {
      const req = { User: this.User, Language: this.getLanguage() || 'en' };
      this.service.GetPartnersMasterData(req).subscribe((response: any) => {
        if (response.Status === 1) {
          this.defaultPhasesMasterData = response.PartnerAgentOrderPhases || [];
        }
      });
    }

    const request = { User: this.User, Language: this.getLanguage() || 'en', AgentID: agent.ID };
    this.loading = true;
    this.service.GetPartnerAgentDefaultPhases(request).subscribe((response: any) => {
      this.loading = false;
      if (response.Status === 1) {
        agent.OrderPhases = response.Phases || [];
        this.selectedDefaultPhaseIDs = (response.Phases || []).map((p: any) => p.ID);
      }
      this.showDefaultPhasesPopup = true;
    });
  }

  closeDefaultPhasesPopup(): void {
    this.showDefaultPhasesPopup = false;
  }

  isDefaultPhaseSelected(phase: any): boolean {
    return this.selectedDefaultPhaseIDs.includes(phase.ID);
  }

  toggleDefaultPhase(phase: any): void {
    const idx = this.selectedDefaultPhaseIDs.indexOf(phase.ID);
    if (idx >= 0) {
      this.selectedDefaultPhaseIDs.splice(idx, 1);
    } else {
      this.selectedDefaultPhaseIDs.push(phase.ID);
    }
  }

  saveDefaultPhases(): void {
    if (!this.selectedDefaultPhaseAgent || this.savingDefaultPhases) return;

    const request = {
      User: this.User,
      Language: this.getLanguage() || 'en',
      AgentID: this.selectedDefaultPhaseAgent.ID,
      PhaseIDs: this.selectedDefaultPhaseIDs
    };

    this.savingDefaultPhases = true;
    this.service.AddUpdatePartnerAgentDefaultPhases(request).subscribe((response: any) => {
      this.savingDefaultPhases = false;
      if (response.Status === 1) {
        this.selectedDefaultPhaseAgent.OrderPhases = response.Phases || [];
        const match = this.PartnerAgents.find((a: any) => a.ID === this.selectedDefaultPhaseAgent.ID);
        if (match) match.OrderPhases = response.Phases || [];
        this.openSnackBar(response.Message || 'Default phases saved', 'OK', 'light-info-snackbar');
        this.closeDefaultPhasesPopup();
      } else {
        this.openSnackBar(response.Message || 'Error saving default phases', 'OK', 'alert-info-snackbar');
      }
    });
  }

}
